/* eslint-disable react/prop-types */
import { DRIVE_TAB } from "@/utils/constants/tabConstants";
import { createPortal } from "react-dom";

import {
  CustomSelect,
  UnifiedDateTimePicker,
  InputWithMediaInfo,
  InputWithVideoPreview,
  Button,
  SelectedMediaCard,
  Tooltip,
  toast,
  TextInput,
} from "@/components";
import { getPlatformIcon } from "@/utils/utilFunctions/commmonFunctions";
import { formatEntityName } from "@/utils/utilFunctions/entityName";
import {
  MediaDetailConfig,
  MediaDetailFeatureMapKey,
} from "@/utils/utilFunctions/Mapper/MediaDetailConfig";
import {
  getContributorFeatures,
  ContributorFeatureMapKey,
} from "@/utils/utilFunctions/Mapper/ContributorConfig";
import {
  MediaEditableConfig,
  MediaEditableFeatureMapKey,
} from "@/utils/utilFunctions/Mapper/EditableConfig";
import {
  secondsToHms,
  scrollToFirstError,
  getHostLogoStyle,
  getHostLogoStyleByHostId,
  getDenmarkStatusIndicatorData,
  socialPublishStatusToTextAndColor,
  getDenmarkMediaUrlWithSeopath,
} from "@/utils";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import MediaThumbnail from "@/components/MediaDetail/MediaThumbnail";
import ArticleSearchResults from "@/components/ArticleSearch/ArticleSearchResults";
import ArticleSearchModal from "@/components/ArticleSearch/ArticleSearchModal";
import ArticlePreviewModal from "@/components/ArticleSearch/ArticlePreviewModal";
import {
  MEDIA_STATUS,
  ALLOWED_FILE_EXTENSION,
} from "@/utils/constants/uiConstants";
import {
  MediaModel,
  Option,
  AssetItem,
  InputWithMediaInfoRef,
  UserModel,
  defaultTeamModel,
  PresetListModel,
  SimulivePayload,
  GenericSocialPublish,
  ProductModel,
  AllHostsList,
  AllAdminPresets,
} from "@/database";
import {
  TagKeyword,
  MANUAL_TAG_KEYWORD_DEFAULTS,
} from "@/components/ChipInput/TagsChipInput";
import { openVideoInNewTab } from "@/utils";
import { generateBaseUrl, getThumbnailUrlUtil } from "@/utils/mediaUtils";
import { store } from "../../../store";
import {
  callDenmarkDetailApi,
  callDenmarkArticleGetApi,
} from "@/store/upload/upload.action";
import { useDispatch, useSelector } from "react-redux";
import Modal from "@/components/Modal/Modal";
import DenmarkPublishDetails from "@/components/MediaPublish/DenmarkPublish/DenmarkPublishDetails";
import PlaylistPublish, {
  type PlaylistGuard,
  type AutoYoutubeChannel,
} from "@/components/MediaPublish/PlaylistPublish/PlaylistPublish";
import AllSocialPublish, {
  type SocialGuard,
} from "@/components/MediaPublish/SocialPublish/SocialPublish";
import { DenmarkPublish as DenmarkPublishInterface } from "@/database";
import DenmarkPublishPopover from "@/components/MediaPublish/DenmarkPublish/DenmarkPublishPopover";
import EmbedForm from "@/components/Create/EmbedForm";
import SimuliveForm from "@/components/Create/SimuliveForm";
import PremiereSchedulePanel, {
  PremiereData,
} from "@/components/Create/PremiereSchedulePanel";
import SandboxIcon from "@/assets/icons/svg/hourglass.svg?react";
import { formatTimestampToAgo } from "../../../utils";
import CreateMetaPreset from "@/components/MetaPresets/CreateMetaPreset";

import SelectPreset from "@/components/MetaPresets/SelectPreset";
import { Search, RefreshCw } from "lucide-react";
import { truncateText } from "@/utils/utilFunctions/truncateText";
import TitleAndDesc from "@/components/MediaDetail/TitleAndDesc";
import ContributorsAndCreditors from "@/components/MediaDetail/ContributorsAndCreditors";
import MediaCommonFields from "@/components/MediaDetail/MediaCommonFields";
import MediaDetailPlayer from "@/components/MediaDetail/MediaDetailPlayer";
import DailymotionIcon from "@/assets/icons/svg/dailymotion-icon.svg?react";
import AlertDialog from "@/utils/ui/AlertDialog/AlertDialog";
import { Alert } from "@/utils/ui/Alert/Alert";
import { useDebounce } from "@/utils";
import {
  setParentMediaList,
  clearBorrowedMediaSocials,
  clearBorrowedMediaPlaylists,
  setMediaReplacing,
} from "@/store/media/media.slice";
import { useAppSelector } from "../../../store";
import { AllHostsInfo, BunchCategoryModel, HostSubSection } from "@/database";
import { AdminPresetListState, Host } from "@/store/media/media.models";
import {
  getAdminPresetList,
  getSearchedListMedia,
  getKeywords,
  getCategoryTags,
  fetchHosts,
  getMedia,
  reuploadMedia,
  getChunkInfo,
} from "@/store/media/media.action";
import {
  addFile,
  increaseCounter,
  decreaseCounter,
  updateFileProgress,
  setUploadedSuccessfully,
  setUploadingError,
} from "@/store/upload/upload.slice";
import { showUploadProgress } from "@/store/app/app.slice";
import { runMediaReupload } from "@/utils/createOptionsUtils";
import { syncDenmarkAccessApi } from "@/database/user/api";
import { usePresetManagement } from "@/components/MediaDetail/hooks/usePresetManagement";
import { useDenmarkPublish } from "@/components/MediaDetail/hooks/useDenmarkPublish";
import { useMediaDetailsFormValidation } from "@/components/MediaDetail/hooks/useMediaDetailsFormValidation";
import { useArticleSearch } from "@/components/MediaDetail/hooks/useArticleSearch";
import Skeleton from "@/components/Skeleton/Skeleton";
import { AdminPreset } from "@/database";
import {
  shouldShowField,
  isFieldMandatoryStrict,
  isFieldMandatoryLoose,
} from "@/utils/mediautils/policyHelpers";
import {
  anyVisibleRightSection,
  buildMediaUpdatePayload,
  deduplicateContributorOptions,
  getScheduleTime,
  mediaSocialsToPresetSocialPayload,
} from "@/utils/mediautils/CommonFunctions";

/**
 * 1) Start from `currentMediaDetailsForEdit.denmark_keywords` (order and objects preserved).
 * 2) For each `tags` name not already present by `keywordName`, append a row with the four
 *    standard fields (`keyEnum`, `keywordName`, `relevance`, `source`).
 */
function mergeTagsIntoDenmarkKeywords(
  denmarkKeywords: TagKeyword[] | undefined,
  tags: string[] | undefined
): TagKeyword[] {
  const result: TagKeyword[] = Array.isArray(denmarkKeywords)
    ? denmarkKeywords.map((k) => ({ ...k }))
    : [];
  const seen = new Set(result.map((k) => k.keywordName).filter(Boolean));

  for (const raw of Array.isArray(tags) ? tags : []) {
    const name = String(raw).trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    result.push({
      ...MANUAL_TAG_KEYWORD_DEFAULTS,
      keywordName: name,
    });
  }
  return result;
}

export interface MediaDetailsForm {
  title: string;
  desc: string;
  tags: string[];
  category_tags: string[];
  stitle: string[];
  business: string;
  team: string;
  lang: string;
  meta_lang: string;
  producer: string[];
  agency: string;
  title_url: string;
  category: string;
  subcategory: string;
  expiry: number;
  is_short: number;
  is_campaign: number;
  landscapeThumb: string;
  landscapeThumbId: string;
  socialThumb: string;
  socialThumbId: string;
  posterThumb: string;
  posterThumbId: string;
  portraitThumb: string;
  portraitThumbId: string;
  podcastThumb: string;
  premiereThumb: string;
  premiereThumbId: string;
  premiereVThumb: string;
  premiereVThumbId: string;
  podcastThumbId: string;
  videocredit: string[];
  audiocredit: string[];
  imagecredit: string[];
  youtube_id: string;
  parentid: string;
  status: number;
  state: number;
  assets: AssetItem[];
  ctype: string;
  production_type: string;
  editor: string[];
  script_writer: string[];
  anchor: string[];
  cameraman: string[];
  smeta: boolean;
  photographer: string[];
  researcher: string[];
  compiler: string[];
  curator: string[];
  reporter: string[];
  author: string[];
  isMandatoryMetaFilled?: boolean;
  is_denmark?: boolean;
  denmark?: DenmarkPublishInterface[];
  live_channel?: any[];
  media_state?: number;
  title_url_edited?: string;
  is_title_url_edited?: boolean;
  meta?: any;
  simulive?: SimulivePayload;
  gcl?: string[];
  gct?: number;
  mfk?: number;
  agency_credits?: string[];
  asset_type?: string;
  media_ref?: { parentId?: string; rel?: string };
  currentUserInfo?: UserModel;
  is_social_change?: boolean;
  schedule?: number;
  campaign_id?: string;
  denmark_keywords: TagKeyword[];
  media_schedule?: number;
  playback_mode?: string;
  loop_count?: number;
  sourceInput?: string;
  pub_ts?: number;
  orientation?: number;
  aspect_ratio?: number;
}

export type MediaDetailFormRef = {
  submitForm: () => void;
  undoChanges: () => void;
};

interface MediaDetailsFormProps {
  currentMediaDetailsForEdit: MediaModel;
  categories: Record<string, Option[]>;
  handleSave: any;
  isWizard?: boolean;
  closeHandler?: () => void;
  openPublish?: () => void;
  /** Navigates to the Social Publish section (used by the Add-to-Playlist YouTube chips). */
  onOpenSocialPublish?: () => void;
  getListMedia: any;
  setIsMandatoryMetaFilled: (isMandatoryMetaFilled: boolean) => void;
  addMediaAsset: any;
  deleteMedia: (media: Record<string, any>, action: string) => any;
  assets: AssetItem[];
  contentType: Option[];
  productionType: Option[];
  producers: Option[];
  editors: Option[];
  scriptWriters: Option[];
  anchors: Option[];
  cameramans: Option[];
  currentMediaDetailsId: string;
  denmarkHosts: AllHostsInfo[];
  ctypes: Option[];
  mfcategories: Option[];
  onHasChanges?: (hasChanges: boolean) => void;
  parentData?: MediaModel;
  updateModal: any;
  currentUser: string | undefined;
  handleTempThumbnailImageUrl: (
    url: string | undefined,
    id: string | undefined
  ) => void;
  liveList: Option[];
  parentMediaList: MediaModel[];
  socialPublishList: GenericSocialPublish[];
  audioCreditList: Option[];
  videoCreditList: Option[];
  imageCreditList: Option[];
  is_title_url_edited?: boolean;
  activeCustomUploadTab: string;
  closeModal?: () => void;
  productDetailsFromConstant: ProductModel[];
  presetList: PresetListModel[];
  constUserList?: UserModel[];
  updateIsClearFilter: (params: any) => void;
  updateIsFilter: (params: any) => void;
  clearFilters?: boolean;
  metaPresetData?: AdminPreset;
  getListUser?: (params: any) => void;
  getListAgencies?: (params: any) => void;
  bunchCategoryList?: BunchCategoryModel[];
  bunchLanguageList?: BunchCategoryModel[];
  bunchProductList?: ProductModel[];
  bunchBusinessList?: UserModel[];
  subCategoryList?: any;
  hosts?: Host[];
  hostSubsection?: HostSubSection[];
  defaultTeam?: defaultTeamModel;
  defaultBusiness?: defaultTeamModel;
  contributorsList?: Option[];
  getCurrentPresetDetails?: any;
  allHostsList?: AllHostsList;
  getAdminCurrentPresetDetails?: any;
  adminCurrentPresetDetails?: {
    isLoading: boolean;
    error: string;
    data: AdminPreset;
  };
  adminPresetList?: AllAdminPresets;
  currentPresetDetails?: {
    isLoading: boolean;
    error: string;
    data: PresetListModel;
  };
  updatePublishMedia?: (params: any, cb: (response: any) => void) => void;
  premiereData?: any;
  getGlobalFavSectionList: (params: { host: number }) => Promise<unknown>;
}

const MediaDetailsForm: React.FC<MediaDetailsFormProps> = ({
  currentMediaDetailsForEdit,
  handleSave,
  isWizard,
  assets,
  openPublish,
  onOpenSocialPublish,
  contentType,
  productionType,
  producers,
  editors,
  anchors,
  scriptWriters,
  cameramans,
  onHasChanges,
  addMediaAsset,
  updateModal,
  setIsMandatoryMetaFilled,
  handleTempThumbnailImageUrl,
  liveList,
  parentMediaList,
  socialPublishList,
  audioCreditList,
  videoCreditList,
  imageCreditList,
  closeHandler,
  closeModal,
  productDetailsFromConstant,
  presetList,
  constUserList,
  metaPresetData,
  getListAgencies,
  bunchCategoryList,
  bunchLanguageList,
  bunchProductList,
  subCategoryList,
  hosts,
  defaultTeam,
  contributorsList,
  getCurrentPresetDetails,
  currentPresetDetails,
  denmarkHosts,
  hostSubsection,
  allHostsList,
  adminPresetList,
  getAdminCurrentPresetDetails,
  adminCurrentPresetDetails,
  updatePublishMedia,
  premiereData,
  currentUser,
  getGlobalFavSectionList,
}) => {
  const rotatingStyles = `
  @keyframes rotate {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(0deg); }
  }
  .rotating-sandbox {
    animation: rotate 2s infinite linear;
    display: inline-block;
    width: 16px;
    height: 16px;
  }
`;

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = rotatingStyles;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const [userEditedTitleUrl, setUserEditedTitleUrl] = useState<boolean>(
    currentMediaDetailsForEdit?.is_title_url_edited ?? false
  );
  const [isTitleUrlLoading, setIsTitleUrlLoading] = useState<{
    title: boolean;
    description: boolean;
    customPrompt: boolean;
    titleUrl: boolean;
  }>({
    title: false,
    description: false,
    customPrompt: false,
    titleUrl: false,
  });
  const [thumbSent, setThumbSent] = useState<string>("");
  const [isPlayerInitialized, setIsPlayerInitialized] =
    useState<boolean>(false);

  useEffect(() => {
    const mandatoryFields = [
      "title",
      "business",
      "team",
      "ctype",
      "agency",
      "category",
      "lang",
      "teamion_type",
    ];
    const hasAllMandatoryFields = mandatoryFields.every(
      (field) =>
        currentMediaDetailsForEdit[
          field as keyof typeof currentMediaDetailsForEdit
        ]
    );

    setIsMandatoryMetaFilled(hasAllMandatoryFields);
  }, [currentMediaDetailsForEdit]);

  const [selectedParentVideo, setSelectedParentVideo] = useState<any>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const mediaInputRef = useRef<InputWithMediaInfoRef>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalContent, setModalContent] = useState<React.ReactNode | null>(
    null
  );
  const [inputMediaText, setInputMediaText] = useState<string>("");

  const debouncedInputMediaText = useDebounce(inputMediaText, 200);
  const [isSaveAndPublishClicked, setIsSaveAndPublishClicked] =
    useState<boolean>(false);
  const playlistGuardRef = useRef<PlaylistGuard | null>(null);
  const [autoYoutubeChannels, setAutoYoutubeChannels] = useState<
    AutoYoutubeChannel[]
  >([]);
  const socialGuardRef = useRef<SocialGuard | null>(null);
  const [playlistError, setPlaylistError] = useState<boolean>(false);
  const [publishDirty, setPublishDirty] = useState<boolean>(false);
  const [denmarkDirty, setDenmarkDirty] = useState<boolean>(false);
  const [ytSelectedChannelNames, setYtSelectedChannelNames] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (debouncedInputMediaText.length >= 3) {
      store.dispatch(
        getSearchedListMedia({
          stitle: debouncedInputMediaText,
          key: "parentid",
          must_not: {
            "media_ref.rel": ["premiere", "embed", "clone", "trimmed"],
            id: currentMediaDetailsForEdit?.id,
          },
        })
      );
    } else {
      store.dispatch(
        setParentMediaList({
          data: { list: [], count: 0 },
          error: null,
          isLoading: false,
        })
      );
    }
  }, [debouncedInputMediaText]);

  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState<boolean>(false);
  const [isSimuliveModalOpen, setIsSimuliveModalOpen] =
    useState<boolean>(false);
  const [simuliveData, setSimuliveData] = useState<any>(
    currentMediaDetailsForEdit?.simulive
  );
  const [mediaRatio, setMediaRatio] = useState<string>("");
  const [localSocials, setLocalSocials] = useState<any[]>([]);
  const [selectedAgency_credits, setSelectedAgency_credits] = useState<
    UserModel[]
  >([]);
  const [selectedagency, setSelectedagency] = useState<UserModel[]>([]);
  const [selectedteam, setSelectedteam] = useState<UserModel[]>([]);
  const [borrowedContributorCredit, setBorrowedContributorCredit] = useState<
    { label: string; value: string }[] | undefined
  >(undefined);
  const [borrowedCreditData, setBorrowedCreditData] = useState<
    { label: string; value: string }[] | undefined
  >(undefined);

  const [selectedProduct, setSelectedProduct] = useState<ProductModel | null>(
    null
  );

  const [autoSelectAgency, setAutoSelectAgency] = useState<UserModel[]>([]);
  const [autoSelectAgencyCredit, setAutoSelectAgencyCredit] = useState<
    UserModel[]
  >([]);

  useEffect(() => {
    const rel = currentMediaDetailsForEdit?.media_ref?.rel;
    if (
      rel === "premiere" ||
      currentMediaDetailsForEdit?.asset_type === "premiere"
    ) {
      setMediaRatio("premiere");
    } else if (
      rel === "podcast" ||
      currentMediaDetailsForEdit?.asset_type === "audio"
    ) {
      setMediaRatio("podcast");
    } else if (
      currentMediaDetailsForEdit?.meta?.width <
      currentMediaDetailsForEdit?.meta?.height
    ) {
      setMediaRatio("portrait");
    } else {
      setMediaRatio("landscape");
    }
  }, [currentMediaDetailsForEdit]);

  // const [updatedLiveList, setUpdatedLiveList] = useState<any[]>([
  //   ...liveList,
  //   ...(currentMediaDetailsForEdit?.live_channel || []),
  // ]);

  const assetType = currentMediaDetailsForEdit?.asset_type || "";
  const isContentType2 = currentMediaDetailsForEdit?.content_type === 2;
  const isDriveAsset =
    assetType === "library" || (assetType === "video" && isContentType2);
  const resolvedAssetType = isDriveAsset ? DRIVE_TAB : assetType;

  const mediaDetailConfig = MediaDetailConfig(
    resolvedAssetType as MediaDetailFeatureMapKey
  );

  const isSocialPublishBlocked =
    !mediaDetailConfig.social_publish_destination.enabled ||
    currentMediaDetailsForEdit?.source === "embed" ||
    currentMediaDetailsForEdit?.media_ref?.rel === "embed" ||
    !!currentMediaDetailsForEdit?.simulive ||
    currentMediaDetailsForEdit?.media_ref?.rel === "premiere";

  const PLAYLIST_ALLOWED_BY_TYPE: Record<string, boolean> = {
    slates: false,
    embed: true,
    campaign: true,
    premiere: false,
  };
  const mediaTypeForPlaylist =
    currentMediaDetailsForEdit?.source === "embed"
      ? "embed"
      : currentMediaDetailsForEdit?.media_ref?.rel === "embed"
        ? "embed"
        : currentMediaDetailsForEdit?.media_ref?.rel === "premiere"
          ? "premiere"
          : currentMediaDetailsForEdit?.asset_type || "";
  const playlistAllowedForType =
    PLAYLIST_ALLOWED_BY_TYPE[mediaTypeForPlaylist] ?? true;

  const isDefaultHost =
    defaultTeam?.settings?.host !== "" &&
    defaultTeam?.settings?.host !== "none";
  const defaultHost = defaultTeam?.settings?.host || "";

  const isDenmarkAccessMissing =
    isDefaultHost &&
    !!defaultHost &&
    !(hosts || []).some(
      (host) => host?.id?.toString() === defaultHost.toString()
    );

  const denmarkKeywordsAvailable = isDefaultHost && !isDenmarkAccessMissing;

  const contributorConfig = getContributorFeatures(
    isDefaultHost
      ? ("denmark" as ContributorFeatureMapKey)
      : ("no_denmark" as ContributorFeatureMapKey)
  );

  const mediaType = currentMediaDetailsForEdit?.media_ref?.rel || "";

  const mediaEditableConfig = MediaEditableConfig(
    mediaType as MediaEditableFeatureMapKey
  );

  // Create a ref to store formik instance for validation schema
  const formikRef = useRef<any>(null);

  // Create validation schema using hook
  // Use a callback to get media_state from formik when validation runs
  const validationSchema = useMediaDetailsFormValidation({
    assetType: resolvedAssetType,
    mediaRatio,
    selectedProduct,
    getMediaState: () => formikRef.current?.values?.media_state,
    isContentType2,
    isDefaultHost,
    isDenmarkAccessMissing,
  });

  const [popoverChip, setPopoverChip] = useState<{
    chip: DenmarkPublishInterface;
    anchor: DOMRect;
  } | null>(null);

  const [articlePopoverChip, setArticlePopoverChip] = useState<{
    chip: any;
    anchor: DOMRect;
  } | null>(null);

  const [embedUpdateKey, setEmbedUpdateKey] = useState<any>({ id: "" });

  const isError = useRef(false);

  const handleEmbedUpdated = (id: any) => {
    setEmbedUpdateKey(id);
    formik.setFieldValue("youtube_id", id?.id);
  };

  const dispatch = useDispatch();

  // Tracked in redux (keyed by media id) rather than component state, so the
  // in-progress indicator survives navigating away from and back to this page.
  const replacingMediaIds = useAppSelector(
    (state) => state.media.replacingMediaIds
  );
  const isReplacingMedia = !!replacingMediaIds[currentMediaDetailsForEdit.id];

  // Right after clicking Replace, the server briefly echoes the media's OLD
  // state:10/status:5 (ready) back over the socket before the new file's
  // processing actually kicks in - so within a live session we only treat a
  // later state:10/status:5 as "reprocessing finished" once we've first seen
  // the media leave the ready state. But if the page was just (re)mounted
  // while a replace was already in flight (e.g. the user navigated back),
  // Media.tsx's mount effect already ran a fresh getMedia, so whatever
  // state/status that returned is authoritative - no need to wait for a
  // leave-then-return transition in that case.
  const hasLeftReadyStateForReplaceRef = useRef(isReplacingMedia);

  const handleReplaceFileSelected = async (file: File) => {
    const mediaId = currentMediaDetailsForEdit.id;
    hasLeftReadyStateForReplaceRef.current = false;
    dispatch(setMediaReplacing({ id: mediaId, isReplacing: true }));

    await runMediaReupload(currentMediaDetailsForEdit, file, {
      reuploadMedia: (params) => dispatch(reuploadMedia(params) as any),
      getMedia: (params) => dispatch(getMedia(params) as any),
      getChunkInfo: (params) => dispatch(getChunkInfo(params) as any),
      addFile: (data) => dispatch(addFile(data)),
      increaseCounter: () => dispatch(increaseCounter()),
      decreaseCounter: () => dispatch(decreaseCounter()),
      showUploadProgress: () => dispatch(showUploadProgress()),
      updateFileProgress: (data) => dispatch(updateFileProgress(data)),
      setUploadedSuccessfully: (data) =>
        dispatch(setUploadedSuccessfully(data)),
      setUploadingError: (data) => dispatch(setUploadingError(data)),
    });
  };

  useEffect(() => {
    if (!isReplacingMedia) return;
    const isReady =
      currentMediaDetailsForEdit.state === 10 &&
      currentMediaDetailsForEdit.status === 5;
    if (!isReady) {
      hasLeftReadyStateForReplaceRef.current = true;
      return;
    }
    if (hasLeftReadyStateForReplaceRef.current) {
      dispatch(
        setMediaReplacing({
          id: currentMediaDetailsForEdit.id,
          isReplacing: false,
        })
      );
    }
  }, [
    isReplacingMedia,
    currentMediaDetailsForEdit.id,
    currentMediaDetailsForEdit.state,
    currentMediaDetailsForEdit.status,
  ]);

  const authUserTeam = useSelector(
    (state: any) => state.auth?.user?.team as string | undefined
  );

  // Socials borrowed from another media via "Reuse Meta" -> select media,
  // to be pre-added as social publish chips for the media being edited.
  const borrowedMediaSocials = useAppSelector(
    (state) => state.media.borrowedMediaSocials
  );

  // Playlists borrowed from another media via "Reuse Meta" -> select media,
  // to be merged into the media being edited's playlist selection.
  const borrowedMediaPlaylists = useAppSelector(
    (state) => state.media.borrowedMediaPlaylists
  );

  const [denmarkRefreshing, setDenmarkRefreshing] = useState(false);
  const [denmarkLastSyncedAt, setDenmarkLastSyncedAt] = useState<number | null>(
    null
  );

  const handleDenmarkRefresh = async () => {
    if (denmarkRefreshing || !currentUser) return;
    setDenmarkRefreshing(true);
    try {
      const res = await syncDenmarkAccessApi(currentUser);
      if (res.ok) {
        setDenmarkLastSyncedAt(Date.now());
        store.dispatch(fetchHosts({}));
        toast.success("Denmark access refreshed");
      } else {
        toast.fail("Couldn't refresh Denmark access");
      }
    } catch {
      toast.fail("Couldn't refresh Denmark access");
    } finally {
      setDenmarkRefreshing(false);
    }
  };

  const handleRouting = async () => {
    if (
      articlePopoverChip &&
      articlePopoverChip?.chip?.job_type === "denmark_embed" &&
      articlePopoverChip?.chip?.status === 10
    ) {
      if (
        articlePopoverChip?.chip?.host &&
        articlePopoverChip?.chip?.published_id
      ) {
        const url = await getDenmarkMediaUrlWithSeopath(
          articlePopoverChip.chip.published_id || "",
          articlePopoverChip.chip.host,
          dispatch,
          callDenmarkDetailApi,
          true,
          callDenmarkArticleGetApi,
          allHostsList
        );
        if (url) {
          window.open(url, "_blank");
        } else {
          toast.fail("Live URL could not be fetched");
        }
      }
    } else {
      if (popoverChip?.chip.status === 10) {
        openVideoInNewTab(
          popoverChip?.chip?.platform,
          popoverChip?.chip?.published_id
        );
      } else return;
    }
  };

  // handlehostChange and handleArticleHostChange are now in useDenmarkPublish hook

  const handleUndoChanges = () => {
    if (hasChanges() || publishDirty || denmarkDirty) {
      if (window.confirm("Are you sure you want to discard your changes?")) {
        formik.resetForm();
        resetDenmarkState(); // Use hook's reset function
        setPublishDirty(false);
        setDenmarkDirty(false);
        if (onHasChanges) {
          onHasChanges(false);
        }
      }
    }
  };

  const statusOptions = currentMediaDetailsForEdit.media_state
    ? [{ label: "Active", value: "1" }]
    : [
        { label: "Draft", value: "0" },
        { label: "Active", value: "1" },
      ];

  // handleDenmarkchange, handleArticleDenmarkchange, and handleAddDenmarkEmbedChip are now in useDenmarkPublish hook

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalContent(null);
    setModalTitle("");
  };

  useEffect(() => {
    const parentData = currentMediaDetailsForEdit?.parentData;

    if (!parentData) return; // 💡 Wait until data is available

    const selectedMedia = {
      label: parentData.id,
      value: parentData.title,
      image: parentData.images?.thumb
        ? getThumbnailUrlUtil({
            media: parentData,
            id: parentData.images.thumb,
            source: parentData?.source,
          })
        : "",
      duration: secondsToHms(parentData.meta?.duration),
      thumb: !!generateBaseUrl(parentData), // true if URL exists
    };

    setSelectedParentVideo(selectedMedia);
  }, [currentMediaDetailsForEdit?.parentData]);
  useEffect(() => {
    // if (currentPresetDetails) return;
    if (isApplied) return;

    const incomingAgency = {
      label: formatEntityName(currentMediaDetailsForEdit?.agencydata),
      value: currentMediaDetailsForEdit?.agencydata?.id,
    };

    const updatedTeamList = [
      {
        label: formatEntityName(currentMediaDetailsForEdit?.teamdata),
        value: currentMediaDetailsForEdit?.teamdata?.id,
      },
    ];

    setSelectedteam(updatedTeamList as unknown as UserModel[]);

    if (incomingAgency.value) {
      setSelectedagency((prev) => {
        const alreadyPresent = prev?.some(
          (item: UserModel) => (item as any)?.value === incomingAgency.value
        );
        if (alreadyPresent) return prev;
        return [...(prev || []), incomingAgency] as unknown as UserModel[];
      });
    }

    const updatedAgencyCreditsList =
      currentMediaDetailsForEdit?.agencyCreditData?.map((val) => {
        return {
          label: ` ${formatEntityName(val)}  ${
            val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
          } `,
          value: val?.id,
        };
      });

    setSelectedAgency_credits(
      updatedAgencyCreditsList as unknown as UserModel[]
    );
  }, [currentMediaDetailsForEdit]);

  const formik = useFormik<MediaDetailsForm>({
    // enableReinitialize: true,
    validateOnChange: true,
    initialValues: {
      title: currentMediaDetailsForEdit?.title || "",
      desc: currentMediaDetailsForEdit.desc || "",
      tags: Array.isArray(currentMediaDetailsForEdit.tags)
        ? currentMediaDetailsForEdit.tags
        : [],
      category_tags: Array.isArray(currentMediaDetailsForEdit.category_tags)
        ? currentMediaDetailsForEdit.category_tags
        : [],
      denmark_keywords: mergeTagsIntoDenmarkKeywords(
        Array.isArray(currentMediaDetailsForEdit.denmark_keywords)
          ? (currentMediaDetailsForEdit.denmark_keywords as unknown as TagKeyword[])
          : [],
        Array.isArray(currentMediaDetailsForEdit.tags)
          ? currentMediaDetailsForEdit.tags
          : []
      ),
      business: currentMediaDetailsForEdit.business || "",
      team: currentMediaDetailsForEdit.team || "",
      lang: currentMediaDetailsForEdit.lang || "",
      meta_lang: currentMediaDetailsForEdit.meta_lang
        ? currentMediaDetailsForEdit.meta_lang.charAt(0).toUpperCase() +
          currentMediaDetailsForEdit.meta_lang.slice(1)
        : "",
      landscapeThumb: getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.thumb,
        source: currentMediaDetailsForEdit?.source,
      }),
      landscapeThumbId: currentMediaDetailsForEdit?.images?.thumb,

      posterThumb: getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.poster,
        source: currentMediaDetailsForEdit?.source,
      }),
      premiereThumb: getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.premiere,
        source: currentMediaDetailsForEdit?.source,
      }),
      premiereThumbId: currentMediaDetailsForEdit?.images?.premiere,
      premiereVThumb: getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.premiere_v,
        source: currentMediaDetailsForEdit?.source,
      }),
      premiereVThumbId: currentMediaDetailsForEdit?.images?.premiere_v,
      posterThumbId: currentMediaDetailsForEdit?.images?.poster,
      portraitThumb: getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.portrait,
        source: currentMediaDetailsForEdit?.source,
      }),
      portraitThumbId: currentMediaDetailsForEdit?.images?.portrait,
      socialThumb: getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.social,
        source: currentMediaDetailsForEdit?.source,
      }),
      socialThumbId: currentMediaDetailsForEdit?.images?.social,
      title_url:
        currentMediaDetailsForEdit.title_url ||
        currentMediaDetailsForEdit.title ||
        "",
      is_title_url_edited:
        currentMediaDetailsForEdit?.is_title_url_edited ?? false,
      title_url_edited:
        currentMediaDetailsForEdit.title_url_edited ||
        currentMediaDetailsForEdit.title_url ||
        currentMediaDetailsForEdit.title,
      producer: currentMediaDetailsForEdit.producer || [],
      agency: currentMediaDetailsForEdit.agency || "",
      is_short:
        currentMediaDetailsForEdit.is_short !== undefined &&
        currentMediaDetailsForEdit.is_short !== null
          ? currentMediaDetailsForEdit.is_short
          : currentMediaDetailsForEdit.asset_type === "shorts"
            ? 1
            : 0,
      is_campaign: currentMediaDetailsForEdit.is_campaign || 1,
      campaign_id: currentMediaDetailsForEdit.campaign_id || "",
      category: currentMediaDetailsForEdit.category || "",
      subcategory: currentMediaDetailsForEdit.subcategory || "",
      expiry: currentMediaDetailsForEdit.expiry || 0,
      youtube_id: currentMediaDetailsForEdit.youtube_id || "",
      parentid: currentMediaDetailsForEdit.parentid || "",
      state: currentMediaDetailsForEdit.state,
      status: currentMediaDetailsForEdit.status,
      assets: assets,
      videocredit: currentMediaDetailsForEdit.videocredit || [],
      audiocredit: currentMediaDetailsForEdit.audiocredit || [],
      imagecredit: currentMediaDetailsForEdit.imagecredit || [],
      ctype: currentMediaDetailsForEdit.ctype || "",
      production_type: currentMediaDetailsForEdit.production_type || "",
      editor: currentMediaDetailsForEdit.editor || [],
      script_writer: currentMediaDetailsForEdit.script_writer || [],
      anchor: currentMediaDetailsForEdit.anchor || [],
      cameraman: currentMediaDetailsForEdit.cameraman || [],
      photographer: currentMediaDetailsForEdit.photographer || [],
      researcher: currentMediaDetailsForEdit.researcher || [],
      compiler: currentMediaDetailsForEdit.compiler || [],
      curator: currentMediaDetailsForEdit.curator || [],
      reporter: currentMediaDetailsForEdit.reporter || [],
      author: currentMediaDetailsForEdit.author || [],
      stitle: currentMediaDetailsForEdit.stitle || [],
      smeta: currentMediaDetailsForEdit.smeta || false,
      media_schedule: currentMediaDetailsForEdit.media_schedule || 0,
      playback_mode: currentMediaDetailsForEdit.playback_mode || "simulive",
      loop_count: currentMediaDetailsForEdit.loop_count || 1,
      meta: currentMediaDetailsForEdit.meta || {},
      ...(currentMediaDetailsForEdit?.simulive && {
        simulive: currentMediaDetailsForEdit?.simulive,
      }),
      is_denmark: false,
      denmark: [],
      orientation: currentMediaDetailsForEdit?.orientation,
      aspect_ratio: currentMediaDetailsForEdit?.aspect_ratio,
      // live_channel:
      //   currentMediaDetailsForEdit?.live_channel?.map((item: any) => {
      //     return item.id;
      //   }) || [],
      media_state: currentMediaDetailsForEdit?.media_state || 0,
      gcl: currentMediaDetailsForEdit?.gcl || [],
      gct: currentMediaDetailsForEdit?.gct,
      mfk: currentMediaDetailsForEdit?.mfk,
      sourceInput: "",
      agency_credits: currentMediaDetailsForEdit?.agency_credits || [],
      asset_type: currentMediaDetailsForEdit?.asset_type || "",
      media_ref: currentMediaDetailsForEdit?.media_ref || {},
      pub_ts: currentMediaDetailsForEdit?.pub_ts || 0,
      podcastThumb: getThumbnailUrlUtil({
        media: currentMediaDetailsForEdit,
        id: currentMediaDetailsForEdit?.images?.podcast,
        source: currentMediaDetailsForEdit?.source,
      }),
      podcastThumbId: currentMediaDetailsForEdit?.images?.podcast,
      is_social_change:
        currentMediaDetailsForEdit?.images?.is_social_change || false,
      schedule:
        currentMediaDetailsForEdit?.socials?.find(
          (item: any) => item.job_type === "denmark"
        )?.schedule || 0,
    },
    validationSchema,
    onSubmit: (values) => {
      const socialError = socialGuardRef.current?.validateStaged?.({
        mediaWillBeActive: Number(values.media_state) === 1,
      });
      if (socialError) {
        toast.fail(socialError);
        return;
      }
      values.title = values.title?.trim() ?? values.title;
      values.desc = values.desc?.trim() ?? values.desc;
      values.title_url = values.title_url?.trim() ?? values.title_url;
      values.title_url_edited =
        values.title_url_edited?.trim() ?? values.title_url_edited;
      const data = buildMediaUpdatePayload({
        values,
        initialValues: formik.initialValues,
        mediaId: currentMediaDetailsForEdit.id,
        currentMediaState: currentMediaDetailsForEdit?.media_state,
        currentState: currentMediaDetailsForEdit?.state,
        currentStatus: currentMediaDetailsForEdit?.status,
        currentImages: currentMediaDetailsForEdit?.images,
        currentSimulive: currentMediaDetailsForEdit?.simulive,
        simuliveData,
        currentDenmark: currentMediaDetailsForEdit?.denmark || [],
        currentSelectedSection,
        isSaveAndPublishClicked,
        userEditedTitleUrl,
        currentTitleUrl: currentMediaDetailsForEdit?.title_url,
        currentTitleUrlEdited: currentMediaDetailsForEdit?.title_url_edited,
        isDefaultHost,
        photographerPolicy: selectedProduct?.contributor_policy?.photographer,
        socials: currentMediaDetailsForEdit?.socials || [],
      });
      const reorderedPlaylists =
        playlistGuardRef.current?.getReorderedPlaylists?.() || [];
      if (reorderedPlaylists.length > 0) {
        (data as any).create_playlists = reorderedPlaylists.map(
          ({ category, duration, ...rest }) => ({
            ...rest,
            artist:
              values.category ||
              category ||
              currentMediaDetailsForEdit?.category ||
              "",
            duration: duration != null ? String(duration) : "",
          })
        );
      }
      const selectedPlaylists =
        playlistGuardRef.current?.getSelectedPlaylists?.() || [];
      const playlistsChanged =
        playlistGuardRef.current?.hasChanges?.() ?? false;
      if (selectedPlaylists.length > 0 || playlistsChanged) {
        (data as any).playlists = selectedPlaylists;
      }
      const removedPlaylists =
        playlistGuardRef.current?.getRemovedPlaylists?.() || [];
      if (removedPlaylists.length > 0) {
        (data as any).remove_playlists = removedPlaylists;
      }

      // If there is exactly one published denmark social and currentSelectedSection
      // has items, merge them into multi_publish_targets and call socialpublish.update.
      const publishedSocials: any[] = currentMediaDetailsForEdit?.socials || [];
      const publishedDenmarkSocials = publishedSocials.filter(
        (s) => s.job_type === "denmark"
      );
      const ctForSort = (s: any) => {
        const n = Number(s?.ct);
        return Number.isFinite(n) ? n : Infinity;
      };
      const oldestPublishedDenmarkSocial =
        publishedDenmarkSocials.length === 0
          ? undefined
          : publishedDenmarkSocials.reduce(
              (oldest, s) => (ctForSort(s) < ctForSort(oldest) ? s : oldest),
              publishedDenmarkSocials[0]
            );

      if (
        publishedDenmarkSocials.length >= 1 &&
        currentSelectedSection.length > 0 &&
        oldestPublishedDenmarkSocial
      ) {
        // Group child chips by which published parent they belong to.
        // Chips with no _parentSectionId fall back to the oldest parent by ct (epoch).
        const childrenByParent = new Map<
          number,
          typeof currentSelectedSection
        >();
        for (const item of currentSelectedSection) {
          const parentSection =
            (item as any)._parentSectionId ??
            oldestPublishedDenmarkSocial.section;
          if (!childrenByParent.has(parentSection)) {
            childrenByParent.set(parentSection, []);
          }
          childrenByParent.get(parentSection)!.push(item);
        }

        for (const [parentSection, chips] of childrenByParent) {
          const publishedParent =
            publishedDenmarkSocials.find(
              (s: any) => s.section === parentSection
            ) ?? oldestPublishedDenmarkSocial;

          const newMultiTargets = chips.map((item) => ({
            section: item.section,
            parent_id: item.section,
            channel_name: item.channel_name,
            host: item.host,
            folder_path: item.folder_path,
            open_story_at: "PUBLISH_SECTION",
            open_newly_in_browser: false,
            isOverRideLinkRequired: true,
            contentPublishType: "MULTIPUBLISH",
          }));

          const existingTargets: any[] = Array.isArray(
            publishedParent.multi_publish_targets
          )
            ? publishedParent.multi_publish_targets
            : [];
          const newSections = new Set(newMultiTargets.map((t) => t.section));
          const mergedTargets = [
            ...existingTargets.filter((t: any) => !newSections.has(t.section)),
            ...newMultiTargets,
          ];

          const updatePayload = {
            ...publishedParent,
            multi_publish_targets: mergedTargets,
          };

          try {
            updatePublishMedia?.(updatePayload as any, () => {});
          } catch (err) {
            console.error("updatePublishMedia failed:", err);
          }
        }

        data.denmark = [];
      } else if (
        publishedDenmarkSocials.length >= 1 &&
        currentMediaDetailsForEdit?.media_state === 0
      ) {
        const newUpdatePayload = {
          ...oldestPublishedDenmarkSocial,
          title: values.title,
          desc: values.desc,
          title_for_url: values.title_url,
          tags: values.tags,
          denmark_req: {
            title_for_url: values.title_url,
          },
        };

        try {
          updatePublishMedia?.(newUpdatePayload as any, () => {});
        } catch (err) {
          console.error("updatePublishMedia (isActive=false) failed:", err);
        }
      }

      handleSave(data, isWizard)
        .then(() => {
          socialGuardRef.current?.publishStaged?.({
            mediaWillBeActive: Number(values.media_state) === 1,
          });
          // Fire deferred deletes of saved Denmark chips now that the form is saved.
          flushDenmarkDeletes(dispatch);

          const currentDenmark = values.denmark || [];
          const temporaryEmbedIds = new Set(
            currentDenmark
              .filter(
                (d: any) => d?.job_type === "denmark_embed" && d?.status === 100
              )
              .map((d: any) => d?.id?.toString() || d?.section?.toString())
          );
          const updatedDenmark = currentDenmark.filter(
            (d: any) => !(d?.job_type === "denmark_embed" && d?.status === 100)
          );
          if (temporaryEmbedIds.size > 0) {
            setSelectedArticles((prevArticles: any[]) => {
              const updatedArticles = prevArticles.filter(
                (a: any) =>
                  !temporaryEmbedIds.has(a.id?.toString()) &&
                  !temporaryEmbedIds.has(a.msid?.toString())
              );
              return updatedArticles;
            });
          }
          const cleanedValues = {
            ...values,
            denmark: updatedDenmark,
          };
          formik.resetForm({ values: cleanedValues });
          formik.setValues(cleanedValues);
          formik.setTouched({});
          setPublishDirty(false);
          setDenmarkDirty(false);
          if (onHasChanges) {
            onHasChanges(false);
          }

          const wasUnpublished = currentMediaDetailsForEdit.media_state === 0;
          const isNowPublished =
            values.media_state === 1 && isSaveAndPublishClicked;

          if (wasUnpublished && isNowPublished && !isWizard) {
            toast.success("Content is active");
          } else if (wasUnpublished && !isNowPublished && !isWizard) {
            toast.success("Metadata saved successfully");
          } else if (!isWizard) {
            toast.success("Metadata updated successfully");
          }

          setCurrentSelectedSection([]);
          setEditedSections([]);
          // setSelectedSections([]);

          // Only proceed with wizard navigation on successful save

          if (isWizard && !isError.current) {
            const isPremiere =
              currentMediaDetailsForEdit?.media_ref?.rel === "premiere";
            if (!isSocialPublishBlocked && !isPremiere) {
              openPublish?.();
            } else if (isSocialPublishBlocked || isPremiere) {
              closeHandler?.();
            } else {
              closeModal?.();
            }
          }
        })
        .catch(() => {
          toast.fail("");

          isError.current = true;
          return;
        });
    },
  });

  // Store formik in ref for validation schema access
  formikRef.current = formik;

  // Denmark publish hook - must be called first since preset hook needs setCurrentSelectedSection
  const {
    currentSelectedSection,
    editedSections,
    selectedHost,
    selectedArticleHost,
    selectedSections,
    selectedArticleSections,
    showDenmarkRemovalAlert,
    setCurrentSelectedSection,
    setEditedSections,
    setSelectedSections,
    setShowDenmarkRemovalAlert,
    setPendingVideoshowRemovalId,
    handlehostChange,
    handleArticleHostChange,
    handleDenmarkchange,
    handleArticleDenmarkchange,
    handleAddDenmarkEmbedChip,
    handleCancelOrDelete,
    handleConfirmDenmarkRemoval,
    queueDenmarkDelete,
    flushDenmarkDeletes,
    createDenmarkEmbedSocialItem,
    resetDenmarkState,
  } = useDenmarkPublish({
    formik,
    currentMediaDetailsForEdit,
    hosts,
    hostSubsection,
    defaultTeam,
    onHasChanges,
    onDenmarkDirty: () => setDenmarkDirty(true),
    selectedArticles: [], // Will be set by article search hook
    setSelectedArticles: () => {}, // Will be set by article search hook
    getGlobalFavSectionList,
  });

  // Article search hook - must be called after Denmark hook to access selectedArticleHost and createDenmarkEmbedSocialItem
  const {
    openSuggestArticles,
    articleSearchQuery,
    selectedArticles,
    articlePreviewModal,
    isFetchingArticlePreview,
    setOpenSuggestArticles,
    setArticleSearchQuery,
    setSelectedArticles,
    handleArticleSearchQuery,
    handleArticlePreview,
    handleCloseArticlePreview,
    handleBackToSearchFromPreview,
    handleSelectedArticlesChange,
    handleSelectArticle,
  } = useArticleSearch({
    formik,
    selectedArticleHost,
    createDenmarkEmbedSocialItem,
    currentMediaDetailsForEdit,
    localSocials,
    setLocalSocials,
  });

  const {
    isOpenPreviewPreset,
    mediaData,
    selectedPresetDetails,
    isApplied,
    openSelectPreset,
    handleSelectPreset,
    handleNext,
    handlesetMediaData,
    handleThumbToggle,
    handleSocialToggle,
    handlePlaylistToggle,
    handleNextClick,
    setOpenSelectPreset,
    closePreviewPreset,
  } = usePresetManagement({
    adminPresetList: adminPresetList || {},
    presetList: presetList || [],
    getCurrentPresetDetails,
    formik,
    setCurrentSelectedSection, // Now using Denmark hook's setter
    currentMediaDetailsForEdit,
    onHasChanges,
    getAdminCurrentPresetDetails,
  });

  // handleSelectedArticlesChange and handleSelectArticle are now in useArticleSearch hook

  useEffect(() => {
    if (!isApplied) return;
    if (!adminCurrentPresetDetails) return;
    let updatedAgencyList = [
      {
        label: formatEntityName(adminCurrentPresetDetails?.data?.agencydata),
        value: adminCurrentPresetDetails?.data?.agencydata?.id,
      },
    ];
    updatedAgencyList = updatedAgencyList || [];

    const updatedTeamList = [
      {
        label: formatEntityName(adminCurrentPresetDetails?.data?.teamdata),
        value: adminCurrentPresetDetails?.data?.teamdata?.id,
      },
    ];

    setSelectedteam(updatedTeamList as unknown as UserModel[]);

    setSelectedagency(
      (prev) =>
        deduplicateContributorOptions([
          ...(prev || []),
          ...(updatedAgencyList as unknown as UserModel[]),
        ]) as unknown as UserModel[]
    );

    const updatedAgencyCreditsList =
      adminCurrentPresetDetails?.data?.agencyCreditData?.map((val: any) => {
        return {
          label: ` ${formatEntityName(val)}  ${
            val?.email ? "(" + truncateText(val?.email, 20) + ")" : ""
          } `,
          value: val?.id,
        };
      }) || [];

    setSelectedAgency_credits(
      (prev) =>
        deduplicateContributorOptions([
          ...(prev || []),
          ...(updatedAgencyCreditsList as unknown as UserModel[]),
        ]) as unknown as UserModel[]
    );

    // Applying a "Reuse Meta" preset sets formik.parentid to the id string
    // (via applyPresetData's generic passthrough), but the SelectedMediaCard
    // display is separately derived from currentMediaDetailsForEdit.parentData
    // (this media's OWN parent, see the effect above) - that doesn't change
    // just because a preset was applied, so the card would otherwise keep
    // showing stale/no data. Seed it here from the applied preset/media's own
    // parentData (carried through mapMediaToPresetPayload) instead.
    const appliedParentData = (adminCurrentPresetDetails?.data as any)
      ?.parentData;
    if (appliedParentData) {
      setSelectedParentVideo({
        label: appliedParentData.id,
        value: appliedParentData.title,
        image: appliedParentData.images?.thumb
          ? getThumbnailUrlUtil({
              media: appliedParentData,
              id: appliedParentData.images.thumb,
              source: appliedParentData?.source,
            })
          : "",
        duration: secondsToHms(appliedParentData.meta?.duration),
        thumb: !!generateBaseUrl(appliedParentData),
      });
    }
  }, [adminCurrentPresetDetails, isApplied]);

  const handleOpenMenu = () => {
    setTimeout(() => {
      mediaInputRef.current?.openMenu();
    }, 0);
  };

  const handleCloseMediaCard = () => {
    setSelectedParentVideo(null);
    formik.setFieldValue("parentid", "");
    setIsEdit(false);
  };

  const handleEditMediaCard = () => {
    handleOpenMenu();
    setIsEdit(true);
  };

  useEffect(() => {
    if (currentMediaDetailsForEdit?.socials) {
      setLocalSocials([...currentMediaDetailsForEdit.socials]);
    }
  }, [currentMediaDetailsForEdit?.socials]);

  const handleDetailsSave = async (
    e?: React.FormEvent,
    willPublish = false
  ) => {
    if (e) {
      e.preventDefault();
    }
    if (willPublish) {
      await formik.setFieldValue("media_state", 1, false);
    }

    Object.keys(formik.initialValues).forEach((field) => {
      formik.setFieldTouched(field as keyof typeof formik.initialValues, true);
    });

    const errors = await formik.validateForm();

    const mediaWillBeActive =
      willPublish || Number(formik.values.media_state) === 1;

    const socialError = socialGuardRef.current?.validateStaged?.({
      mediaWillBeActive,
    });

    const playlistMissing =
      playlistVisible &&
      playlistMandatory &&
      mediaWillBeActive &&
      (playlistGuardRef.current?.getSelectedPlaylists?.() || []).length === 0 &&
      (playlistGuardRef.current?.getReorderedPlaylists?.() || []).length === 0;
    setPlaylistError(playlistMissing);

    const hasFormErrors = Object.keys(errors).length > 0;

    if (hasFormErrors) {
      setTimeout(() => {
        scrollToFirstError(errors);
      }, 100);
    }

    if (socialError) {
      toast.fail(socialError);
    }
    if (playlistMissing) {
      toast.fail("Please add the media to at least one playlist.");
    }
    if (!isWizard && (hasFormErrors || socialError || playlistMissing)) {
      return;
    }
    if (socialError || playlistMissing) {
      return;
    }

    formik.submitForm();
  };

  const handleDetailsSaveRef = useRef(handleDetailsSave);
  handleDetailsSaveRef.current = handleDetailsSave;

  useEffect(() => {
    const handleNextClick = () => {
      if (!isWizard) {
        setIsSaveAndPublishClicked(false);
      }

      handleDetailsSaveRef.current();
    };

    window.addEventListener("nextClick", handleNextClick);
    return () => {
      window.removeEventListener("nextClick", handleNextClick);
    };
  }, [isWizard]);

  useEffect(() => {
    const handleNextAndPublishClick = () => {
      setIsSaveAndPublishClicked(true);
      handleDetailsSaveRef.current(undefined, true);
    };

    window.addEventListener("next&PublishClick", handleNextAndPublishClick);
    return () => {
      window.removeEventListener(
        "next&PublishClick",
        handleNextAndPublishClick
      );
    };
  }, []);

  useEffect(() => {
    const undoChanges = () => {
      handleUndoChanges();
    };

    window.addEventListener("undoClick", undoChanges);
    return () => {
      window.removeEventListener("undoClick", undoChanges);
    };
  }, [handleUndoChanges]);

  const sortObjectProperties = (obj: any): any => {
    if (obj === null || typeof obj !== "object") {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(sortObjectProperties);
    }

    return Object.keys(obj)
      .sort()
      .reduce((result: any, key) => {
        result[key] = sortObjectProperties(obj[key]);
        return result;
      }, {});
  };

  const hasChanges = useCallback(() => {
    const initialValues = formik.initialValues;
    const currentValues = formik.values;

    const formHasChanges = Object.keys(initialValues).some((key) => {
      const initial = initialValues[key as keyof MediaDetailsForm];
      const current = currentValues[key as keyof MediaDetailsForm];
      if (Array.isArray(initial) && Array.isArray(current)) {
        return JSON.stringify(initial) !== JSON.stringify(current);
      }
      if (
        typeof initial === "object" &&
        initial !== null &&
        typeof current === "object" &&
        current !== null
      ) {
        return JSON.stringify(initial) !== JSON.stringify(current);
      }
      return initial !== current;
    });

    const initialDenmark = currentMediaDetailsForEdit?.denmark || [];
    const denmarkHasChanges =
      JSON.stringify(initialDenmark) !== JSON.stringify(currentSelectedSection);

    const initialSimulive =
      currentMediaDetailsForEdit?.simulive || ({} as SimulivePayload);
    let simuliveHasChanges = false;

    if (initialSimulive?.parentMediaId) {
      const sortedInitialSimulive = sortObjectProperties(initialSimulive);
      const sortedSimuliveData = sortObjectProperties(simuliveData);
      simuliveHasChanges =
        JSON.stringify(sortedInitialSimulive) !==
        JSON.stringify(sortedSimuliveData);
    }

    return formHasChanges || denmarkHasChanges || simuliveHasChanges;
  }, [
    formik.initialValues,
    formik.values,
    currentMediaDetailsForEdit?.denmark,
    currentMediaDetailsForEdit?.simulive,
    currentSelectedSection,
    simuliveData,
  ]);

  useEffect(() => {
    if (onHasChanges) {
      onHasChanges(hasChanges() || publishDirty || denmarkDirty);
    }
  }, [
    formik.values,
    formik.initialValues,
    onHasChanges,
    publishDirty,
    denmarkDirty,
  ]);

  const handleSimuliveUpdated = (id: any) => {
    if (onHasChanges) {
      onHasChanges(true);
    }

    // setEmbedUpdateKey(id);
  };

  const renderStatusIndicator = (status: number) => {
    if ([1, 2, 3, 4, 7].includes(status)) {
      return <SandboxIcon className="rotating-sandbox" />;
    }

    return (
      <span
        className={`rounded-circle d-inline-block ${
          socialPublishStatusToTextAndColor(status).statusColor
        }`}
        style={{
          height: "10px",
          width: "10px",
        }}
      />
    );
  };

  useEffect(() => {
    if (bunchProductList) {
      const product = bunchProductList.find(
        (product: ProductModel) => product.id === formik.values.team
      );
      setSelectedProduct(product || null);
    }
  }, [bunchProductList, formik.values.team]);

  const denmarkContainerShows =
    !isContentType2 &&
    currentMediaDetailsForEdit?.asset_type !== "library" &&
    isDefaultHost;
  const isSimuliveChild = !!currentMediaDetailsForEdit?.simulive?.parentMediaId;
  const playlistPolicy = selectedProduct?.third_party_policy?.playlist;
  const playlistVisible =
    !isSimuliveChild &&
    playlistAllowedForType &&
    shouldShowField(playlistPolicy);
  const playlistMandatory = isFieldMandatoryStrict(playlistPolicy);
  const socialVisible = !isSocialPublishBlocked;

  const publishSubLabel = (text: string) => (
    <div
      className="text-uppercase fw-medium text-secondary"
      style={{ fontSize: 11, letterSpacing: ".04em" }}
    >
      {text}
    </div>
  );

  const orientation = currentMediaDetailsForEdit?.orientation;
  const liveThumb = formik.values.socialThumbId
    ? {
        thumb: formik.values.socialThumb,
        thumb_id: formik.values.socialThumbId,
      }
    : orientation === 2
      ? {
          thumb: formik.values.portraitThumb,
          thumb_id: formik.values.portraitThumbId,
        }
      : {
          thumb: formik.values.landscapeThumb,
          thumb_id: formik.values.landscapeThumbId,
        };

  const liveMediaForSocial = useMemo(
    () => ({
      title: formik.values.title,
      desc: formik.values.desc,
      tags: formik.values.tags,
      thumb: liveThumb.thumb,
      thumb_id: liveThumb.thumb_id,
    }),
    [
      formik.values.title,
      formik.values.desc,
      formik.values.tags,
      liveThumb.thumb,
      liveThumb.thumb_id,
    ]
  );

  const borrowedSocialPresetDetails = useMemo(() => {
    if (
      !borrowedMediaSocials?.sourceMediaId ||
      borrowedMediaSocials.sourceMediaId === currentMediaDetailsForEdit?.id
    ) {
      return undefined;
    }
    return mediaSocialsToPresetSocialPayload(borrowedMediaSocials.socials);
  }, [borrowedMediaSocials, currentMediaDetailsForEdit?.id]);

  useEffect(() => {
    if (borrowedSocialPresetDetails) {
      dispatch(clearBorrowedMediaSocials());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [borrowedSocialPresetDetails]);

  const borrowedPlaylistsTargetIdRef = useRef<string | undefined>(undefined);
  if (
    borrowedMediaPlaylists?.sourceMediaId &&
    borrowedPlaylistsTargetIdRef.current === undefined
  ) {
    borrowedPlaylistsTargetIdRef.current = currentMediaDetailsForEdit?.id;
  }

  const mergedSavedPlaylists = useMemo(() => {
    const own = (currentMediaDetailsForEdit as any)?.playlists || [];
    if (
      !borrowedMediaPlaylists?.sourceMediaId ||
      borrowedPlaylistsTargetIdRef.current !== currentMediaDetailsForEdit?.id
    ) {
      return own;
    }
    const ownIds = new Set(own.map((p: any) => p?.id));
    const borrowed = (borrowedMediaPlaylists.playlists || []).filter(
      (p: any) => !ownIds.has(p?.id)
    );
    return borrowed.length > 0 ? [...own, ...borrowed] : own;
  }, [borrowedMediaPlaylists, currentMediaDetailsForEdit]);

  const mergedPlaylistsData = useMemo(() => {
    const own = (currentMediaDetailsForEdit as any)?.playlistsData || [];
    if (
      !borrowedMediaPlaylists?.sourceMediaId ||
      borrowedPlaylistsTargetIdRef.current !== currentMediaDetailsForEdit?.id
    ) {
      return own;
    }
    const ownIds = new Set(own.map((p: any) => p?.id));
    const borrowed = (borrowedMediaPlaylists.playlistsData || []).filter(
      (p: any) => !ownIds.has(p?.id)
    );
    return borrowed.length > 0 ? [...own, ...borrowed] : own;
  }, [borrowedMediaPlaylists, currentMediaDetailsForEdit]);

  useEffect(() => {
    if (
      borrowedMediaPlaylists?.sourceMediaId &&
      borrowedPlaylistsTargetIdRef.current !== undefined &&
      borrowedPlaylistsTargetIdRef.current !== currentMediaDetailsForEdit?.id
    ) {
      borrowedPlaylistsTargetIdRef.current = undefined;
      dispatch(clearBorrowedMediaPlaylists());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMediaDetailsForEdit?.id]);

  const socialSection = socialVisible ? (
    <div>
      {publishSubLabel("Social Publish")}
      <AllSocialPublish
        embedded
        liveMedia={liveMediaForSocial}
        autoYoutubeChannels={autoYoutubeChannels}
        presetDetails={borrowedSocialPresetDetails}
        socialGuardRef={socialGuardRef}
        onYoutubeSelectionChange={setYtSelectedChannelNames}
        onDirty={() => setPublishDirty(true)}
      />
    </div>
  ) : null;

  const handlePlaylistDenmarkSections = useCallback(
    (
      sections: {
        section: number;
        channelName: string;
        host?: number;
        folderPath?: string;
      }[]
    ) => {
      // Only stage a section if we actually have access to its host.
      const accessibleHostIds = new Set(
        (hosts || []).map((h) => h?.id?.toString())
      );
      const usable = sections.filter(
        (s) => s.host != null && accessibleHostIds.has(String(s.host))
      );
      if (usable.length === 0) return;
      handlehostChange({ value: String(usable[0].host) }, "select");
      usable.forEach((s) => {
        handleDenmarkchange(
          {
            label: `${s.section} | ${s.channelName}`,
            value: s.section,
            path: s.folderPath || "",
            host: s.host,
          },
          {}
        );
      });
    },
    [hosts, handlehostChange, handleDenmarkchange]
  );

  const playlistSection = playlistVisible ? (
    <div>
      <PlaylistPublish
        inlineHeading={publishSubLabel("Playlist")}
        required={playlistMandatory}
        error={playlistError}
        onSelectionChange={(count) => {
          if (count > 0) setPlaylistError(false);
        }}
        mediaId={currentMediaDetailsForEdit?.id}
        mediaTitle={currentMediaDetailsForEdit?.title}
        mediaThumbnail={getThumbnailUrlUtil({
          media: currentMediaDetailsForEdit,
          id: currentMediaDetailsForEdit?.images?.thumb,
          source: currentMediaDetailsForEdit?.source,
        })}
        mediaDuration={currentMediaDetailsForEdit?.meta?.duration}
        mediaCategory={currentMediaDetailsForEdit?.category}
        team={formik.values.team || authUserTeam}
        savedPlaylists={mergedSavedPlaylists}
        playlistsData={mergedPlaylistsData}
        mediaState={currentMediaDetailsForEdit?.media_state ?? 0}
        onOpenSocialPublish={onOpenSocialPublish}
        guardRef={playlistGuardRef}
        onYoutubeChannelsChange={
          isSocialPublishBlocked ? undefined : setAutoYoutubeChannels
        }
        priorityChannelNames={ytSelectedChannelNames}
        denmarkEnabled={denmarkKeywordsAvailable}
        onDenmarkSectionsChange={handlePlaylistDenmarkSections}
        onDirty={() => setPublishDirty(true)}
        hideHeader
      />
    </div>
  ) : null;

  return (
    <>
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          title={modalTitle}
          width="770px"
        >
          {modalContent}
        </Modal>
      )}
      {openSelectPreset && (
        <Modal
          isOpen={openSelectPreset}
          onClose={() => setOpenSelectPreset(false)}
          title="Reuse details from a previous  video or preset"
          width="990px"
          closeOnOutsideClick={true}
          height="95%"
        >
          <SelectPreset
            type="meta_preset"
            handleSelectPreset={handleSelectPreset}
            isWizard={!!isWizard}
          />
        </Modal>
      )}
      <ArticleSearchModal
        isOpen={openSuggestArticles}
        onClose={() => setOpenSuggestArticles(false)}
        initialQuery={articleSearchQuery}
        hostid={selectedArticleHost}
        onArticlePreview={handleArticlePreview}
        selectedArticles={selectedArticles}
        onSelectedArticlesChange={handleSelectedArticlesChange}
        mediaId={currentMediaDetailsForEdit?.id}
        onAddDenmarkEmbedChip={handleAddDenmarkEmbedChip}
        formikDenmarkValues={formik.values.denmark || []}
        denmarkHosts={denmarkHosts}
      />
      <ArticlePreviewModal
        isOpen={articlePreviewModal.isOpen}
        article={articlePreviewModal.article}
        articleDetails={articlePreviewModal.articleDetails}
        loading={articlePreviewModal.loading}
        error={articlePreviewModal.error}
        onClose={handleCloseArticlePreview}
        onSetAsLead={() => {
          handleSelectArticle(articlePreviewModal.article, "lead");
          handleCloseArticlePreview();
          setArticleSearchQuery("");
        }}
        onSetAsEmbed={() => {
          handleSelectArticle(articlePreviewModal.article, "embed");
          handleCloseArticlePreview();
          setArticleSearchQuery("");
        }}
        onAddDenmarkEmbedChip={(article, type) =>
          handleAddDenmarkEmbedChip(article, type)
        }
        hostid={selectedArticleHost}
        openedFromInlineSearch={articlePreviewModal.openedFromInlineSearch}
        inlineSearchQuery={articlePreviewModal.inlineSearchQuery}
        onBackToSearch={handleBackToSearchFromPreview}
      />
      {isFetchingArticlePreview &&
        createPortal(
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.45)", zIndex: 2050 }}
          >
            <div className="spinner-border text-light" role="status">
              <span className="visually-hidden">
                Fetching article details...
              </span>
            </div>
          </div>,
          document.body
        )}
      <AlertDialog
        isOpen={showDenmarkRemovalAlert}
        onClose={() => {
          setShowDenmarkRemovalAlert(false);
          setPendingVideoshowRemovalId(null);
        }}
        onConfirm={handleConfirmDenmarkRemoval}
        title="ALERT!"
        message="If you remove the videoshow, the articleshow will also be removed. Do you want to continue?"
        confirmText="Confirm"
        cancelText="Cancel"
        confirmButtonType="warning"
      />

      {currentMediaDetailsForEdit.media_state !== 1 &&
        currentMediaDetailsForEdit?.asset_type !== "slate" &&
        currentMediaDetailsForEdit?.content_type &&
        currentMediaDetailsForEdit?.content_type !== 2 && (
          <button
            className="floating-btn btn btn-outline-primary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpenSelectPreset(true);
            }}
          >
            {/* <RotateCcw size={20} className="icon" /> */}
            Reuse Meta
          </button>
        )}

      {isOpenPreviewPreset && (
        <Modal
          isOpen={isOpenPreviewPreset}
          onClose={closePreviewPreset}
          title="Preview Media or Preset"
          height="100%"
          width="990px"
          footer={
            <div className="d-flex justify-content-between align-items-center gap-4 w-100">
              <Button
                type="outline-primary"
                onClick={() => {
                  closePreviewPreset();
                  setOpenSelectPreset(true);
                }}
              >
                Back
              </Button>
              <div className="d-flex align-items-center gap-2">
                <span
                  className="text-warning px-3 py-2 rounded border border-warning"
                  style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                >
                  Your changes will get override by this meta.
                </span>
                <Button
                  onClick={() => {
                    handleNextClick();
                  }}
                >
                  Apply
                </Button>
              </div>
            </div>
          }
        >
          {adminCurrentPresetDetails?.isLoading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Skeleton type="table" count={4} />
            </div>
          ) : (
            <CreateMetaPreset
              currentMediaDetailsForEdit={currentMediaDetailsForEdit}
              isPreview={true}
              selectedMedia={mediaData as AdminPreset | null | undefined}
              item={selectedPresetDetails}
              handleSetMediaData={handlesetMediaData}
              handleNext={handleNext}
              onThumbToggle={handleThumbToggle}
              onSocialToggle={handleSocialToggle}
              onPlaylistToggle={handlePlaylistToggle}
            />
          )}
        </Modal>
      )}

      <form
        className="mb-20 h-100 z-1"
        onKeyDown={(e) => {
          if (
            e.key === "Enter" &&
            (e.target as HTMLElement).tagName !== "TEXTAREA"
          ) {
            e.preventDefault();
          }
        }}
      >
        {currentMediaDetailsForEdit?.media_status ===
          MEDIA_STATUS.MEDIA_STATUS_DRAFT &&
          isWizard &&
          currentMediaDetailsForEdit?.content_type &&
          currentMediaDetailsForEdit?.content_type !== 2 && (
            <Alert
              variant="warning"
              className="py-2 d-flex align-items-center position-fixed bottom-0 z-2"
              style={{ right: "280px" }}
            >
              Your content is in Draft. Set it to Active to publish.
              <i
                className="bi bi-caret-right-fill position-absolute f-20"
                style={{ right: "-15px", color: "#ffe59c" }}
              ></i>
            </Alert>
          )}
        <div
          className=""
          style={{
            maxHeight: isWizard ? "calc(100% - 70px)" : "calc(100% - 65px)",
          }}
        >
          <div className="row m-0">
            <div className={`col-sm-7 col-md-8 col-lg-9 p-0 pb-5`}>
              <div className="row gy-3 m-0">
                <TitleAndDesc
                  selectedProduct={selectedProduct}
                  formik={formik}
                  currentMediaDetailsForEdit={currentMediaDetailsForEdit}
                  mediaDetailConfig={mediaDetailConfig}
                  mediaEditableConfig={mediaEditableConfig}
                  isWizard={isWizard}
                  metaPresetData={metaPresetData}
                  productDetailsFromConstant={productDetailsFromConstant}
                  userEditedTitleUrl={userEditedTitleUrl}
                  setUserEditedTitleUrl={setUserEditedTitleUrl}
                  isTitleUrlLoading={isTitleUrlLoading}
                  setIsTitleUrlLoading={setIsTitleUrlLoading}
                  isContentType2={isContentType2}
                />
                <MediaCommonFields
                  formik={formik}
                  currentMediaDetailsForEdit={currentMediaDetailsForEdit}
                  mediaDetailConfig={mediaDetailConfig}
                  mediaEditableConfig={mediaEditableConfig}
                  selectedProduct={selectedProduct}
                  contentType={contentType}
                  productionType={productionType}
                  bunchCategoryList={bunchCategoryList}
                  bunchLanguageList={bunchLanguageList}
                  bunchProductList={bunchProductList}
                  subCategoryList={subCategoryList}
                  defaultTeam={defaultTeam}
                  getListAgencies={getListAgencies}
                  selectedagency={selectedagency}
                  setSelectedagency={setSelectedagency}
                  selectedteam={selectedteam}
                  setSelectedteam={setSelectedteam}
                  autoSelectAgency={autoSelectAgency}
                  setAutoSelectAgency={setAutoSelectAgency}
                  getKeywords={
                    denmarkKeywordsAvailable
                      ? (hostId, text) =>
                          store.dispatch(
                            getKeywords(hostId, text) as Parameters<
                              typeof store.dispatch
                            >[0]
                          )
                      : undefined
                  }
                  showKeywordFetchLoader={denmarkKeywordsAvailable}
                  isContentType2={isContentType2}
                  getCategoryTags={
                    denmarkKeywordsAvailable
                      ? (host, query) =>
                          store.dispatch(getCategoryTags(host, query) as any)
                      : undefined
                  }
                />

                <MediaThumbnail
                  formik={formik}
                  currentMediaDetailsForEdit={currentMediaDetailsForEdit}
                  assets={assets}
                  mediaDetailConfig={mediaDetailConfig}
                  mediaEditableConfig={mediaEditableConfig}
                  mediaRatio={mediaRatio}
                  isWizard={isWizard}
                  updateModal={updateModal}
                  handleTempThumbnailImageUrl={handleTempThumbnailImageUrl}
                  setThumbSent={setThumbSent}
                  addMediaAsset={addMediaAsset}
                />

                {!isContentType2 && (
                  <ContributorsAndCreditors
                    selectedProduct={selectedProduct}
                    contributorConfig={contributorConfig}
                    formik={formik}
                    currentMediaDetailsForEdit={currentMediaDetailsForEdit}
                    mediaDetailConfig={mediaDetailConfig}
                    mediaEditableConfig={mediaEditableConfig}
                    contributorsList={contributorsList}
                    constUserList={constUserList}
                    currentPresetDetails={currentPresetDetails}
                    adminCurrentPresetDetails={adminCurrentPresetDetails}
                    isApplied={isApplied}
                    producers={producers}
                    editors={editors}
                    anchors={anchors}
                    scriptWriters={scriptWriters}
                    cameramans={cameramans}
                    audioCreditList={audioCreditList}
                    videoCreditList={videoCreditList}
                    imageCreditList={imageCreditList}
                    borrowedContributorCredit={borrowedContributorCredit}
                    borrowedCreditData={borrowedCreditData}
                    getListAgencies={getListAgencies}
                    selectedAgency_credits={selectedAgency_credits}
                    autoSelectAgencyCredit={autoSelectAgencyCredit}
                    setAutoSelectAgencyCredit={setAutoSelectAgencyCredit}
                    isContentType2={isContentType2}
                  />
                )}
              </div>
            </div>

            <div className={`side_form col-sm-5 col-md-4 col-lg-3`}>
              {/* Premiere: if no source video, replace player with schedule panel */}
              {(() => {
                const isPremiereMedia =
                  currentMediaDetailsForEdit?.media_ref?.rel === "premiere";
                const parentId =
                  currentMediaDetailsForEdit?.media_ref?.parentId;
                const basePanelData: PremiereData = {
                  parentMediaId: parentId ?? null,
                  sourceInput: formik.values.sourceInput ?? "",
                  media_schedule: formik.values.media_schedule ?? 0,
                  playbackMode:
                    (formik.values
                      .playback_mode as PremiereData["playbackMode"]) ??
                    "simulive",
                  loopCount: formik.values.loop_count ?? 1,
                };

                if (!isPremiereMedia) {
                  return (
                    <MediaDetailPlayer
                      currentMediaDetailsForEdit={currentMediaDetailsForEdit}
                      isWizard={isWizard}
                      isPlayerInitialized={isPlayerInitialized}
                      setIsPlayerInitialized={setIsPlayerInitialized}
                      thumbSent={thumbSent}
                      embedUpdateKey={embedUpdateKey?.id}
                      formik={formik}
                      statusOptions={statusOptions}
                      setIsSaveAndPublishClicked={setIsSaveAndPublishClicked}
                      isReplacing={isReplacingMedia}
                      replaceAcceptExtensions={ALLOWED_FILE_EXTENSION.VIDEO}
                      onReplaceFileSelected={handleReplaceFileSelected}
                    />
                  );
                }

                const handlePremiereDataChange = (
                  data: Partial<
                    import("@/components/Create/PremiereSchedulePanel").PremiereData
                  >
                ) => {
                  if (data.media_schedule !== undefined)
                    formik.setFieldValue("media_schedule", data.media_schedule);
                  if (data.playbackMode !== undefined)
                    formik.setFieldValue("playback_mode", data.playbackMode);
                  if (data.loopCount !== undefined)
                    formik.setFieldValue("loop_count", data.loopCount);
                  if (data.sourceInput !== undefined)
                    formik.setFieldValue("sourceInput", data.sourceInput);
                  if (data.syncedMetadata !== undefined) {
                    const sm = data.syncedMetadata;
                    if (!sm) return;
                    const stringFields = [
                      "title",
                      "desc",
                      "team",
                      "category",
                      "subcategory",
                      "lang",
                      "agency",
                      "production_type",
                      "ctype",
                    ] as const;
                    stringFields.forEach((field) => {
                      if (sm[field] !== undefined)
                        formik.setFieldValue(field, sm[field]);
                    });
                    const arrayFields = [
                      "tags",
                      "editor",
                      "anchor",
                      "script_writer",
                      "producer",
                      "cameraman",
                      "photographer",
                      "researcher",
                      "compiler",
                      "curator",
                      "reporter",
                      "author",
                      "imagecredit",
                      "videocredit",
                      "audiocredit",
                      "agency_credits",
                    ] as const;
                    arrayFields.forEach((field) => {
                      if (sm[field] !== undefined)
                        formik.setFieldValue(field, sm[field]);
                    });
                    if (sm.tags !== undefined) {
                      formik.setFieldValue(
                        "denmark_keywords",
                        mergeTagsIntoDenmarkKeywords(
                          formik.values.denmark_keywords,
                          sm.tags as string[]
                        )
                      );
                    }
                    if (sm.gcl !== undefined)
                      formik.setFieldValue("gcl", sm.gcl);
                    if (sm.gct !== undefined)
                      formik.setFieldValue("gct", sm.gct);
                    if (sm.mfk !== undefined)
                      formik.setFieldValue("mfk", sm.mfk);
                  }
                  if (data.syncedCreditMeta) {
                    const cm = data.syncedCreditMeta;
                    if (cm.teamCredit?.length)
                      setSelectedteam(cm.teamCredit as unknown as UserModel[]);
                    if (cm.agencyCredit?.length)
                      setSelectedagency(
                        cm.agencyCredit as unknown as UserModel[]
                      );
                    if (cm.contributorCredit?.length)
                      setBorrowedContributorCredit(cm.contributorCredit);
                    if (cm.agencyCreditData?.length)
                      setSelectedAgency_credits(
                        cm.agencyCreditData as unknown as UserModel[]
                      );
                    if (cm.creditData?.length)
                      setBorrowedCreditData(cm.creditData);
                  }
                };

                const damid = currentMediaDetailsForEdit?.damid;
                const hasDamidNoVideo =
                  !!damid &&
                  typeof damid === "string" &&
                  damid.trim() !== "" &&
                  currentMediaDetailsForEdit?.state !== 10;
                const showSchedulePanelOnly = !parentId || hasDamidNoVideo;

                return (
                  <>
                    {showSchedulePanelOnly ? (
                      <>
                        <PremiereSchedulePanel
                          premiereData={basePanelData}
                          hasVideo={false}
                          onDataChange={handlePremiereDataChange}
                          isWizard={isWizard}
                        />
                        {isWizard && (
                          <CustomSelect
                            label="Status"
                            value={String(formik.values.media_state)}
                            handleChange={(selectedOption: any) => {
                              if (
                                selectedOption &&
                                !Array.isArray(selectedOption)
                              ) {
                                formik.setFieldValue(
                                  "media_state",
                                  selectedOption.value
                                );
                                setIsSaveAndPublishClicked(true);
                              }
                            }}
                            options={statusOptions}
                            handleBlur={formik.handleBlur}
                            error={formik.errors.media_state}
                            touched={!!formik.touched.media_state}
                            updatedOptions={statusOptions}
                            className="statusField gap-3 mt-3"
                            maintainSelectHeight="20px"
                            customStyling={{
                              border: "2px solid var(--bg-label-color)",
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <MediaDetailPlayer
                        currentMediaDetailsForEdit={currentMediaDetailsForEdit}
                        isWizard={isWizard}
                        isPlayerInitialized={isPlayerInitialized}
                        setIsPlayerInitialized={setIsPlayerInitialized}
                        thumbSent={thumbSent}
                        embedUpdateKey={embedUpdateKey?.id}
                        formik={formik}
                        statusOptions={statusOptions}
                        setIsSaveAndPublishClicked={setIsSaveAndPublishClicked}
                        isReplacing={isReplacingMedia}
                        replaceAcceptExtensions={ALLOWED_FILE_EXTENSION.VIDEO}
                        onReplaceFileSelected={handleReplaceFileSelected}
                      />
                    )}
                    {parentId && !hasDamidNoVideo && (
                      <PremiereSchedulePanel
                        premiereData={basePanelData}
                        hasVideo={true}
                        onDataChange={handlePremiereDataChange}
                        isWizard={isWizard}
                      />
                    )}
                  </>
                );
              })()}
              <div className="mt-2 z-1">
                {currentMediaDetailsForEdit.source === "embed" && (
                  <div className="border rounded mt-3">
                    <div className="d-flex flex-row justify-content-between align-items-center bgGrey">
                      <div className="p-2 fw-medium text-primary">
                        MEDIA INFO
                      </div>
                    </div>
                    <div className="d-flex flex-column p-2 position-relative">
                      <div className="text-uppercase fw-medium text-primary d-flex flex-column gap-2">
                        {/* <span className='truncate fs-6'>{currentMediaDetailsForEdit?.title}</span> */}
                        <label className="fs-6">
                          {`Embed: ${
                            currentMediaDetailsForEdit?.embed?.type
                          } | ${
                            currentMediaDetailsForEdit?.islive === 1
                              ? "Live"
                              : "Non-Live"
                          } | video`}
                        </label>
                      </div>
                      <i
                        className="bi bi-pencil position-absolute fs-5"
                        onClick={() => {
                          setIsEmbedModalOpen(true);
                        }}
                        style={{ cursor: "pointer", right: "15px" }}
                      />
                      <Modal
                        title="Embed Details"
                        isOpen={isEmbedModalOpen}
                        onClose={() => {
                          setIsEmbedModalOpen(false);
                        }}
                        width="900px"
                        footer={
                          <div className="d-flex justify-content-end">
                            <Button
                              type="outline-primary"
                              onClick={() => {
                                const event = new CustomEvent("nextClickEmbed");
                                window.dispatchEvent(event);
                              }}
                            >
                              Update
                            </Button>
                          </div>
                        }
                      >
                        <EmbedForm
                          fromDetails={true}
                          autoValidateOnMount={true}
                          closeModal={() => {
                            setIsEmbedModalOpen(false);
                          }}
                          onEmbedUpdated={(id: string) => {
                            handleEmbedUpdated(id);
                          }}
                        />
                      </Modal>
                    </div>
                  </div>
                )}
                {currentMediaDetailsForEdit.simulive && isWizard === false && (
                  <div className="border rounded mt-3 p-2 d-flex justify-content-between m-2">
                    <div className="text-uppercase fw-medium text-primary d-flex flex-column gap-2">
                      <span>{currentMediaDetailsForEdit?.title}</span>
                    </div>
                    <i
                      className={`bi bi-pencil ${
                        new Date().getTime() >
                        (currentMediaDetailsForEdit?.simulive?.startTime || 0)
                          ? "text-muted"
                          : ""
                      }`}
                      onClick={() => {
                        if (
                          new Date().getTime() <=
                          (currentMediaDetailsForEdit?.simulive?.startTime || 0)
                        ) {
                          setIsSimuliveModalOpen(true);
                        }
                      }}
                      style={{
                        cursor:
                          new Date().getTime() >
                          (currentMediaDetailsForEdit?.simulive?.startTime || 0)
                            ? "not-allowed"
                            : "pointer",
                        opacity:
                          new Date().getTime() >
                          (currentMediaDetailsForEdit?.simulive?.startTime || 0)
                            ? "0.5"
                            : "1",
                      }}
                    />
                    <Modal
                      title="Simulive Details"
                      isOpen={isSimuliveModalOpen}
                      onClose={() => {
                        setIsSimuliveModalOpen(false);
                      }}
                      width="900px"
                    >
                      <SimuliveForm
                        fromDetails={true}
                        autoValidateOnMount={true}
                        handleSimuliveData={(data: any) => {
                          setSimuliveData(data);
                        }}
                        closeModal={() => {
                          setIsSimuliveModalOpen(false);
                        }}
                        onSimuliveUpdated={(id: string) => {
                          handleSimuliveUpdated(id);
                        }}
                      />
                    </Modal>
                  </div>
                )}

                {!isContentType2 &&
                  currentMediaDetailsForEdit.asset_type !== "library" &&
                  isDefaultHost && (
                    <div className="border rounded mt-3">
                      {/* {mediaDetailConfig.denmark.enabled && ( */}
                      <div className="d-flex flex-row justify-content-between align-items-center bgGrey">
                        <div
                          className="p-2 text-uppercase fw-medium text-primary"
                          style={{
                            color: "back",
                          }}
                        >
                          PUBLISH
                        </div>
                        <Tooltip
                          content={
                            denmarkRefreshing
                              ? "Refreshing Denmark access..."
                              : denmarkLastSyncedAt
                                ? `Refresh Denmark access · Last synced ${formatTimestampToAgo(
                                    denmarkLastSyncedAt
                                  )}`
                                : "Refresh your Denmark access"
                          }
                        >
                          <button
                            type="button"
                            aria-label="Refresh Denmark access"
                            aria-pressed={denmarkRefreshing}
                            onClick={handleDenmarkRefresh}
                            disabled={denmarkRefreshing}
                            className="d-flex align-items-center justify-content-center me-2"
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#6c757d",
                              cursor: denmarkRefreshing ? "default" : "pointer",
                              padding: "6px",
                              borderRadius: "6px",
                              transition:
                                "color 0.15s ease, background 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (denmarkRefreshing) return;
                              (
                                e.currentTarget as HTMLButtonElement
                              ).style.color = "#0d6efd";
                              (
                                e.currentTarget as HTMLButtonElement
                              ).style.background = "#eef2ff";
                            }}
                            onMouseLeave={(e) => {
                              (
                                e.currentTarget as HTMLButtonElement
                              ).style.color = "#6c757d";
                              (
                                e.currentTarget as HTMLButtonElement
                              ).style.background = "transparent";
                            }}
                          >
                            <RefreshCw
                              size={14}
                              strokeWidth={1.7}
                              style={{
                                animation: denmarkRefreshing
                                  ? "denmarkRefreshSpin 0.8s linear infinite"
                                  : undefined,
                                marginRight: "6px",
                              }}
                            />
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 500,
                                letterSpacing: "0.01em",
                              }}
                            >
                              {denmarkRefreshing ? "Syncing..." : "Sync Access"}
                            </span>
                          </button>
                        </Tooltip>
                        <style>
                          {`@keyframes denmarkRefreshSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
                        </style>
                      </div>
                      <Tooltip
                        content={
                          isDenmarkAccessMissing
                            ? "You do not have Denmark access. Please contact your admin."
                            : undefined
                        }
                      >
                        <div className="d-flex flex-column m-3 gap-2">
                          {/* )} */}
                          {mediaDetailConfig.denmark.enabled &&
                            isDefaultHost && (
                              <div
                                style={{
                                  opacity: isDenmarkAccessMissing ? 0.5 : 1,
                                  pointerEvents: isDenmarkAccessMissing
                                    ? "none"
                                    : "auto",
                                }}
                              >
                                <DenmarkPublishDetails
                                  showActions={true}
                                  hostsArray={hosts}
                                  selectedHost={selectedHost}
                                  currentSelectedSection={
                                    currentSelectedSection
                                  }
                                  editedSections={editedSections}
                                  handleDenmarkchange={handleDenmarkchange}
                                  handlehostChange={handlehostChange}
                                  handleCancelOrDelete={handleCancelOrDelete}
                                  onQueueDenmarkDelete={queueDenmarkDelete}
                                  selectedSections={selectedSections}
                                  onSaveAndPublish={setCurrentSelectedSection}
                                  onClearEditedSections={() =>
                                    setEditedSections([])
                                  }
                                  isDraft={
                                    currentMediaDetailsForEdit.media_state === 0
                                  }
                                  schedule_after={formik.values.schedule || 0}
                                  onScheduleAfterChange={(value: number) => {
                                    formik.setFieldValue("schedule", value);
                                  }}
                                />
                              </div>
                            )}
                          <div className="d-flex flex-column position-relative z-2 mt-2">
                            {mediaDetailConfig.denmark.enabled &&
                              isDefaultHost && (
                                <div
                                  style={{
                                    opacity: isDenmarkAccessMissing ? 0.5 : 1,
                                    pointerEvents: isDenmarkAccessMissing
                                      ? "none"
                                      : "auto",
                                  }}
                                >
                                  <DenmarkPublishDetails
                                    selectedHost={selectedArticleHost}
                                    currentSelectedSection={
                                      formik.values.denmark || []
                                    }
                                    handleDenmarkchange={
                                      handleArticleDenmarkchange
                                    }
                                    handlehostChange={handleArticleHostChange}
                                    handleCancelOrDelete={handleCancelOrDelete}
                                    onQueueDenmarkDelete={queueDenmarkDelete}
                                    selectedSections={selectedArticleSections}
                                    useArticleSearch={true}
                                    onArticleSearch={handleArticleSearchQuery}
                                    hostsArray={hosts}
                                    hasInFormDenmark={
                                      Array.isArray(formik.values.denmark) &&
                                      (formik.values.denmark as any[]).some(
                                        (s: any) => s?.job_type === "denmark"
                                      )
                                    }
                                  />
                                </div>
                              )}
                            {hosts && hosts.length > 0 && isDefaultHost && (
                              <ArticleSearchResults
                                searchQuery={articleSearchQuery}
                                onViewAll={() => {
                                  setOpenSuggestArticles(true);
                                  setTimeout(
                                    () => setArticleSearchQuery(""),
                                    100
                                  );
                                }}
                                showInlineResults={true}
                                maxInlineResults={5}
                                hostid={selectedArticleHost}
                                selectedArticles={selectedArticles}
                                onSelectedArticlesChange={
                                  handleSelectedArticlesChange
                                }
                                onArticlePreview={(article) =>
                                  handleArticlePreview(article, true)
                                }
                                mediaId={currentMediaDetailsForEdit?.id}
                                onCloseSearch={() => setArticleSearchQuery("")}
                                onAddDenmarkEmbedChip={
                                  handleAddDenmarkEmbedChip
                                }
                                formikDenmarkValues={
                                  formik.values.denmark || []
                                }
                                denmarkHosts={denmarkHosts}
                              />
                            )}
                          </div>

                          {false && selectedArticles.length > 0 && (
                            <div className="mb-2">
                              <div className="d-flex flex-wrap gap-2">
                                {selectedArticles.map((article: any) => {
                                  const socialItem = localSocials.find(
                                    (social) =>
                                      social.id?.toString() === article.id ||
                                      social.section?.toString() === article.id
                                  );
                                  const isTemporary =
                                    !socialItem || socialItem.status === 100;
                                  const hostToUse =
                                    socialItem?.host?.toString() ||
                                    article.selectedHost ||
                                    selectedArticleHost;

                                  const hostLogoStyle =
                                    getHostLogoStyleByHostId(
                                      hostToUse,
                                      hosts,
                                      undefined,
                                      denmarkHosts
                                    );

                                  return (
                                    <div
                                      key={article.id}
                                      className="social-chip d-flex align-items-center border gap-1 p-1 rounded-pill"
                                      style={{
                                        backgroundColor: "#f8f9fa",
                                        fontSize: "12px",
                                        cursor: isTemporary
                                          ? "default"
                                          : "pointer",
                                        position: "relative",
                                        maxWidth: "46%",
                                      }}
                                      onClick={(e) => {
                                        if (!isTemporary) {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          const rect =
                                            e.currentTarget.getBoundingClientRect();
                                          const chipData = socialItem || {
                                            id: article.id,
                                            channel_name:
                                              article.channel_name ||
                                              article.title,
                                            host: parseInt(
                                              selectedArticleHost,
                                              10
                                            ),
                                            job_type: "denmark_embed",
                                            status: 100,
                                            title: article.title,
                                            desc: article.description,
                                          };
                                          setArticlePopoverChip({
                                            chip: chipData,
                                            anchor: rect,
                                          });
                                        }
                                      }}
                                    >
                                      <div style={hostLogoStyle} />
                                      <span
                                        className="text-truncate flex-grow-1 px-1"
                                        style={{ paddingRight: "10px" }}
                                      >
                                        {article.channel_name ||
                                          article.title ||
                                          "Unknown Article"}
                                      </span>
                                      {isTemporary && (
                                        <i
                                          className="bi bi-x fs-4"
                                          style={{ cursor: "pointer" }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectedArticlesChange(
                                              selectedArticles.filter(
                                                (a: any) => a.id !== article.id
                                              )
                                            );
                                          }}
                                        />
                                      )}
                                      {!isTemporary && socialItem && (
                                        <Tooltip
                                          content={
                                            socialItem?.schedule &&
                                            socialItem.schedule > Date.now() &&
                                            socialItem.status === 10
                                              ? `Scheduled On: ${new Date(
                                                  socialItem.schedule
                                                ).toLocaleString()}`
                                              : socialItem?.status === 10
                                                ? `Published On: ${formatTimestampToAgo(
                                                    socialItem?.schedule ||
                                                      socialItem?.mt
                                                  )}`
                                                : socialItem?.status === -2 ||
                                                    socialItem?.status === -1
                                                  ? (socialItem?.msg ??
                                                    "Your content has failed to publish.")
                                                  : socialItem?.status === 0
                                                    ? "Your content is in queue, uploading will begin once content is activated."
                                                    : "Your content is getting uploaded, currently in progress."
                                          }
                                        >
                                          {socialItem?.schedule &&
                                          socialItem.schedule > Date.now() &&
                                          socialItem.status === 10 ? (
                                            <i
                                              className="bi bi-clock text-secondary me-1"
                                              style={{ fontSize: "1rem" }}
                                            ></i>
                                          ) : (
                                            (() => {
                                              const statusData =
                                                getDenmarkStatusIndicatorData(
                                                  socialItem.status
                                                );

                                              return statusData.type ===
                                                "rotating" ? (
                                                <SandboxIcon
                                                  className={
                                                    statusData.className
                                                  }
                                                  width={16}
                                                  height={16}
                                                  style={{ flexShrink: 0 }}
                                                />
                                              ) : (
                                                <span
                                                  className={
                                                    statusData.className
                                                  }
                                                  style={statusData.style}
                                                />
                                              );
                                            })()
                                          )}
                                        </Tooltip>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {articlePopoverChip && (
                            <DenmarkPublishPopover
                              chip={articlePopoverChip.chip}
                              anchor={articlePopoverChip.anchor}
                              onClose={() => setArticlePopoverChip(null)}
                              handleRouting={handleRouting}
                              publishData={articlePopoverChip.chip}
                              currentMediaDetailsForEdit={
                                currentMediaDetailsForEdit
                              }
                              hostLogoStyle={
                                hosts &&
                                hosts.length > 0 &&
                                hosts.find(
                                  (host: any) =>
                                    host.id ===
                                    articlePopoverChip.chip.host?.toString()
                                )
                                  ? getHostLogoStyle(
                                      hosts.find(
                                        (host: any) =>
                                          host.id ===
                                          articlePopoverChip.chip.host?.toString()
                                      )?.name || ""
                                    )
                                  : {}
                              }
                            />
                          )}
                        </div>
                      </Tooltip>
                      <div className="d-flex flex-column m-3 gap-3">
                        {socialSection}
                        {playlistSection}
                      </div>
                      {/* <div className="d-flex flex-column">
                        <label>Live Channels</label>
                        <CustomSelect
                          label=""
                          placeholder="24/7 Live Channels"
                          value={processPermissionForSelect(
                            formik.values.live_channel || []
                          )}
                          handleChange={(newValue: any) => {
                            handlePermissionChange(
                              newValue.map((option: Option) => option.value),
                              "live_channel"
                            );
                          }}
                          options={updatedLiveList}
                          handleBlur={formik.handleBlur}
                          multiSelect={true}
                          touched={formik.touched.live_channel}
                          hideArrow={false}
                          updatedOptions={updatedLiveList}
                          noOptionsMessage={() => "No destination available"}
                          isNoOptionsMessage={true}
                        />
                      </div> */}
                    </div>
                  )}

                <style>
                  {`
                      .truncate-text {
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                      }
                    `}
                </style>
                {!denmarkContainerShows &&
                  (socialSection || playlistSection) && (
                    <div className="border rounded mt-3">
                      <div className="d-flex flex-row justify-content-between align-items-center bgGrey">
                        <div className="p-2 text-uppercase fw-medium text-primary">
                          PUBLISH ONSITE
                        </div>
                      </div>
                      <div className="d-flex flex-column m-3 gap-2">
                        {socialSection}
                        {playlistSection}
                      </div>
                    </div>
                  )}
                {!isContentType2 &&
                  anyVisibleRightSection(selectedProduct) &&
                  assetType !== "premiere" &&
                  !premiereData && (
                    <div className="border rounded mt-3">
                      <div className="p-3 pt-2 d-flex flex-column gap-3">
                        {mediaDetailConfig.expiry.enabled &&
                          shouldShowField(selectedProduct?.expiry_policy) && (
                            <UnifiedDateTimePicker
                              mode="date"
                              label="Expiry"
                              name="expiry"
                              onBlur={formik.handleBlur}
                              onChange={(date: Date | null) => {
                                formik.setFieldValue(
                                  "expiry",
                                  date ? date.getTime() : null
                                );
                              }}
                              value={formik.values.expiry}
                              touched={!!formik.touched.expiry}
                              error={formik.errors.expiry}
                              startDate={new Date()}
                              disabled={!mediaEditableConfig.expiry}
                              showRequired={isFieldMandatoryStrict(
                                selectedProduct?.expiry_policy
                              )}
                            />
                          )}
                        {!isContentType2 &&
                          mediaDetailConfig.youtube_id.enabled &&
                          shouldShowField(
                            selectedProduct?.youtube_id_policy
                          ) && (
                            <InputWithVideoPreview
                              label="Youtube Id"
                              name="youtube_id"
                              disabled={!mediaEditableConfig.youtube_id}
                              onBlur={formik.handleBlur}
                              onChange={(newval) => {
                                formik.setFieldValue(
                                  "youtube_id",
                                  newval.target.value
                                );
                              }}
                              value={formik.values.youtube_id}
                              touched={formik.touched.youtube_id}
                              placeholder="Enter Id"
                              showRequired={isFieldMandatoryStrict(
                                selectedProduct?.youtube_id_policy
                              )}
                              error={formik.errors.youtube_id}
                            />
                          )}
                        {mediaDetailConfig.campaign_id.enabled &&
                          currentMediaDetailsForEdit?.asset_type ===
                            "campaign" && (
                            <TextInput
                              label="Campaign Id"
                              name="campaign_id"
                              placeholder="Enter Campaign Id"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.campaign_id || ""}
                              touched={formik.touched.campaign_id}
                              error={formik.errors.campaign_id}
                              showRequired={
                                mediaDetailConfig.campaign_id.required
                              }
                            />
                          )}
                        {!isContentType2 &&
                          (currentMediaDetailsForEdit?.media_ref?.rel !==
                            "premiere" &&
                          currentMediaDetailsForEdit?.media_ref?.rel !==
                            "embed" &&
                          selectedParentVideo &&
                          isEdit === false ? (
                            <div className="mt-1 d-flex flex-column gap-1">
                              <label className="form-label mb-1">
                                Parent Video
                              </label>
                              <SelectedMediaCard
                                media={selectedParentVideo}
                                type="pre"
                                onClose={() => handleCloseMediaCard()}
                                onEdit={() => handleEditMediaCard()}
                                showEditButton={true}
                                showDeleteButton={true}
                                showPreviewButton={true}
                                cardStyle={{ width: "80px", minWidth: "80px" }}
                                style={{ width: "220px" }}
                                maxLength={20}
                              />
                            </div>
                          ) : (
                            currentMediaDetailsForEdit?.media_ref?.rel !==
                              "premiere" &&
                            currentMediaDetailsForEdit?.media_ref?.rel !==
                              "embed" &&
                            mediaDetailConfig.parentid.enabled &&
                            shouldShowField(
                              selectedProduct?.parentid_policy
                            ) && (
                              <div className="position-relative ">
                                {/* <Search className="searchInput" size={18} /> */}
                                <InputWithMediaInfo
                                  label="Parent Video"
                                  value={formik.values.parentid}
                                  ref={mediaInputRef}
                                  disabled={!mediaEditableConfig.parentid}
                                  handleChange={(newValue: any) => {
                                    formik.setFieldValue(
                                      "parentid",
                                      newValue?.label
                                    );
                                    setSelectedParentVideo(newValue);
                                    setIsEdit(false);
                                    store.dispatch(
                                      getSearchedListMedia({
                                        stitle: "",
                                        key: "parentid",
                                        must_not: {
                                          "media_ref.rel": [
                                            "premiere",
                                            "embed",
                                            "clone",
                                            "trimmed",
                                          ],
                                          id: currentMediaDetailsForEdit?.id,
                                        },
                                      })
                                    );
                                  }}
                                  options={parentMediaList}
                                  sortMode="fuzzy"
                                  onInputChangeCallback={(params) => {
                                    setInputMediaText(params.keyword);
                                    if (params.keyword.length === 0) {
                                      store.dispatch(
                                        setParentMediaList({
                                          data: { list: [], count: 0 },
                                          error: null,
                                          isLoading: false,
                                        })
                                      );
                                    }
                                  }}
                                  handleBlur={(e) => {
                                    formik.handleBlur(e);
                                    setIsEdit(false);
                                  }}
                                  menuPlacement="bottom"
                                  menuStyles={{
                                    left: "auto",
                                    right: isWizard ? "-25%" : "",
                                  }}
                                  hideArrow={true}
                                  touched={formik.touched.parentid}
                                  initialSelectedOption={
                                    currentMediaDetailsForEdit?.parentData
                                  }
                                  initialStructuredData={selectedParentVideo}
                                  initialInputText={
                                    isEdit && selectedParentVideo
                                      ? String(selectedParentVideo.value ?? "")
                                      : inputMediaText
                                  }
                                  customStyling={{
                                    padding: "0 10px 0 0",
                                  }}
                                  placeholder="Search Title, Id"
                                  loader={true}
                                  showRequired={isFieldMandatoryStrict(
                                    selectedProduct?.parentid_policy
                                  )}
                                  error={formik.errors.parentid}
                                />
                              </div>
                            )
                          ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default MediaDetailsForm;
